import React from 'react';

function signed(value, digits = 2) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${number > 0 ? '+' : ''}${number.toFixed(digits)}`;
}

function LargeMoveDetails({ factor }) {
  const items = factor.anomalies?.items || [];
  if (!items.length) return null;
  return (
    <details className="market-large-move-details">
      <summary>查看主要大幅净值变动日（前{items.length}个）</summary>
      <div className="market-large-move-table-wrap">
        <table>
          <thead><tr><th>净值标签日</th><th>产品变动</th><th>对应行情日</th><th>{factor.label}变动</th><th>数据复算</th></tr></thead>
          <tbody>
            {items.map((item) => (
              <tr key={`${item.market_date}-${item.nav_date}`}>
                <td>{item.nav_date}</td>
                <td className={Number(item.product_pct) < 0 ? 'is-negative' : 'is-positive'}>{signed(item.product_pct, 4)}%</td>
                <td>{item.market_date}</td>
                <td className={Number(item.market_pct) < 0 ? 'is-negative' : 'is-positive'}>{signed(item.market_pct, 4)}%</td>
                <td><span className={`nav-quality-badge quality-${item.quality?.tone || 'muted'}`} title={item.quality?.note}>{item.quality?.label || '待复核'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>识别门槛：偏离产品日变动中位数至少 {Number(factor.anomalies.threshold_pct || 0).toFixed(4)}%。这些字段可能均由同一净值派生，所以复算只检查入库数据未自相矛盾，不是对管理人披露的独立验证。</p>
    </details>
  );
}

function rSquared(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${(number * 100).toFixed(1)}%` : '—';
}

function MultifactorPanel({ model }) {
  if (!model?.available) {
    return (
      <div className="multifactor-panel is-unavailable">
        <div><span>多因子联合解释</span><strong>暂不可建模</strong></div>
        <p>{model?.reason || '共同对齐样本不足。'}</p>
      </div>
    );
  }
  const maxCoefficient = Math.max(...(model.factors || []).map((item) => Math.abs(Number(item.standardized_coefficient) || 0)), 0.001);
  return (
    <div className={`multifactor-panel verdict-${model.verdict?.key || 'unusable'}`}>
      <div className="multifactor-head">
        <div><span>多因子联合解释</span><strong>{model.verdict?.label}</strong></div>
        <p>{model.verdict?.note}</p>
      </div>
      <div className="multifactor-metrics">
        <div><span>训练期R²</span><strong>{rSquared(model.train_metrics?.r_squared)}</strong><small>{model.train_size}个样本</small></div>
        <div><span>样本外R²</span><strong>{rSquared(model.holdout_metrics?.r_squared)}</strong><small>{model.holdout_size}个后续样本</small></div>
        <div><span>样本外平均误差</span><strong>{model.holdout_metrics?.mae_bp != null ? `${Number(model.holdout_metrics.mae_bp).toFixed(2)}bp` : '—'}</strong><small>{model.holdout_start} 至 {model.holdout_end}</small></div>
        <div><span>大幅变动剔除</span><strong>{model.excluded_large_moves}</strong><small>共同对齐 {model.sample_size} 个</small></div>
      </div>
      <div className="multifactor-coefficients">
        <h5>标准化因子系数（仅比较模型内相对影响）</h5>
        {(model.factors || []).map((factor) => (
          <div key={factor.key}>
            <span>{factor.label}</span>
            <i><b style={{ width: `${Math.max(4, (Math.abs(factor.standardized_coefficient) / maxCoefficient) * 100)}%` }} /></i>
            <strong>{signed(factor.standardized_coefficient, 3)} · {factor.direction}</strong>
          </div>
        ))}
      </div>
      <p className="multifactor-limitation">为什么不能精准推算：{model.limitation}</p>
    </div>
  );
}

function FactorCard({ factor }) {
  const available = factor.available;
  const confidence = factor.confidence?.label || '—';
  const rolling = factor.rolling?.windows || [];
  return (
    <article className={`market-sensitivity-card ${available ? '' : 'is-insufficient'}`}>
      <header>
        <div>
          <h4>{factor.label}</h4>
          <p>{factor.code} · {factor.relevance?.label || '相关因子'}</p>
        </div>
        <span className={`sensitivity-confidence confidence-${factor.confidence?.key || 'low'}`}>
          {available ? `${confidence}置信度` : '样本不足'}
        </span>
      </header>

      <div className="market-sensitivity-metrics">
        <div><span>相关系数</span><strong>{available ? signed(factor.correlation, 3) : '—'}</strong><small>{factor.relationship_direction || '暂不判断'} · {factor.strength_label}</small></div>
        <div><span>隐含敏感度 β</span><strong>{available && factor.beta_displayable ? signed(factor.beta, 3) : '—'}</strong><small>{factor.beta_displayable ? '单因子线性拟合' : '关系过弱，不作解释'}</small></div>
        <div><span>方向一致率</span><strong>{factor.direction_consistency_pct != null ? `${Number(factor.direction_consistency_pct).toFixed(1)}%` : '—'}</strong><small>涨跌方向相同的样本</small></div>
        <div><span>对齐样本</span><strong>{factor.sample_size || 0}</strong><small>{factor.sample_start_market_date || '—'} 至 {factor.sample_end_market_date || '—'}</small></div>
      </div>

      <div className="market-sensitivity-stability">
        <div className={`stability-summary stability-${factor.rolling?.key || 'insufficient'}`}>
          <span>滚动稳定性</span>
          <strong>{factor.rolling?.label || '暂无法判断'}</strong>
        </div>
        <div className="stability-windows">
          {rolling.map((item) => (
            <div key={item.window}>
              <span>近{item.window}样本</span>
              <strong>{item.available ? signed(item.correlation, 3) : '—'}</strong>
              <small>{item.available ? `${item.sample_size}个样本` : '样本不足'}</small>
            </div>
          ))}
        </div>
      </div>

      <p className="market-sensitivity-reading">{factor.interpretation}</p>
      <p className="market-sensitivity-stability-note">{factor.rolling?.note}</p>
      {factor.anomalies?.count > 0 ? (
        <p className="market-sensitivity-anomaly">检出 {factor.anomalies.count} 个大幅净值变动样本；其中 {factor.anomalies.quality_summary?.derived_consistent || 0} 个派生字段未自相矛盾（非独立验证）。剔除后相关系数为 {signed(factor.anomalies.robust_correlation, 3)}。</p>
      ) : null}
      <LargeMoveDetails factor={factor} />
      <p className="market-sensitivity-source">净值日期已按产品T+N确认时滞与市场行情日对齐</p>
    </article>
  );
}

export default function ProductMarketSensitivityPanel({ sensitivity }) {
  if (!sensitivity) return <p className="decision-muted">历史响应数据尚未生成。</p>;
  const factors = sensitivity.factors || [];
  if (!factors.length) return <p className="decision-muted">{sensitivity.reason || '暂无与本产品相关的市场因子。'}</p>;

  return (
    <div className="product-market-sensitivity">
      <div className="market-sensitivity-method">
        <span>{sensitivity.confirm_time || 'T+N'} 对齐</span>
        <p>市场T日行情对齐至受影响的净值标签日；当前使用单因子历史线性拟合，结果不进入评分。</p>
      </div>
      <MultifactorPanel model={sensitivity.multifactor} />
      <div className="market-sensitivity-grid">
        {factors.map((factor) => <FactorCard factor={factor} key={`${factor.key}-${factor.code}`} />)}
      </div>
      <p className="market-sensitivity-disclaimer">{sensitivity.disclaimer}</p>
    </div>
  );
}
